import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useInvoices } from "../../../lib/invoices/useInvoices";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

interface TaxRow {
  label: string;
  amountCents: number;
  deductibleCents: number;
}

export function ApprovalAndTax({
  preset,
  submittedBy,
}: {
  preset: RangePreset;
  submittedBy?: string | null;
}) {
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();
  const navigate = useNavigate();

  const { pipeline, tax } = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const counts = { pending: 0, approved: 0, rejected: 0 };
    const taxMap = new Map<string, TaxRow>();

    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      if (e.status === "pending") counts.pending += 1;
      else if (e.status === "approved") counts.approved += 1;
      else if (e.status === "rejected") counts.rejected += 1;

      const label = e.taxPolicyLabel?.trim() || "Uncategorized";
      const row = taxMap.get(label) ?? {
        label,
        amountCents: 0,
        deductibleCents: 0,
      };
      row.amountCents += e.amountCents;
      row.deductibleCents += e.deductibleAmountCents ?? 0;
      taxMap.set(label, row);
    }

    for (const inv of invoices) {
      if (submittedBy && inv.submittedBy !== submittedBy) continue;
      if (!withinRange(inv.issuedOn, startIso, endIso)) continue;
      if (inv.status === "pending") counts.pending += 1;
      else if (inv.status === "approved") counts.approved += 1;
      else if (inv.status === "rejected") counts.rejected += 1;
    }

    const taxRows = Array.from(taxMap.values()).sort(
      (a, b) => b.amountCents - a.amountCents,
    );

    return { pipeline: counts, tax: taxRows };
  }, [expenses, invoices, preset, submittedBy]);

  const pipelineEmpty =
    pipeline.pending === 0 &&
    pipeline.approved === 0 &&
    pipeline.rejected === 0;

  return (
    <Card
      title="Approvals & tax"
      testId="report-approval-tax"
    >
      {pipelineEmpty && tax.length === 0 ? (
        <EmptyState message="No approvals or tax activity in this window yet." />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {(
              [
                {
                  key: "pending",
                  label: "Pending",
                  value: pipeline.pending,
                  color: "text-amber-600",
                },
                {
                  key: "approved",
                  label: "Approved",
                  value: pipeline.approved,
                  color: "text-emerald-600",
                },
                {
                  key: "rejected",
                  label: "Rejected",
                  value: pipeline.rejected,
                  color: "text-rose-600",
                },
              ] as const
            ).map((t) => (
              <button
                type="button"
                key={t.key}
                data-testid={`report-pipeline-${t.key}`}
                onClick={() => navigate("/inbox?tab=pending")}
                className="text-left rounded-md border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100 transition-colors"
              >
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  {t.label}
                </div>
                <div className={`text-2xl font-semibold mt-0.5 ${t.color}`}>
                  {t.value}
                </div>
              </button>
            ))}
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">
              Tax policy summary
            </div>
            {tax.length === 0 ? (
              <div className="text-xs text-slate-500 py-3 text-center">
                No tagged tax policies in this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-2 font-medium">Policy</th>
                      <th className="py-1.5 px-2 font-medium text-right">
                        Spend
                      </th>
                      <th className="py-1.5 pl-2 font-medium text-right">
                        Deductible
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tax.map((row) => (
                      <tr
                        key={row.label}
                        className="border-b border-slate-100 last:border-0"
                      >
                        <td className="py-1.5 pr-2 text-slate-700">
                          {row.label}
                        </td>
                        <td className="py-1.5 px-2 text-right text-slate-900 tabular-nums">
                          {formatCents(row.amountCents)}
                        </td>
                        <td className="py-1.5 pl-2 text-right text-slate-600 tabular-nums">
                          {formatCents(row.deductibleCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
